import React from 'react';
import { Clock, Coffee, Percent, Users } from 'lucide-react';

export interface TechnologyBillingKpiStripProps {
  billable: number;
  nonBillable: number;
  productivityPct: number;
  resourceCount: number;
  periodSummary: string;
  recordCount: number;
}

function fmtHours(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/**
 * Same tile pattern as {@link RegionalKpiStrip} — accent rail, icon chip, uppercase label.
 */
export function TechnologyBillingKpiStrip({
  billable,
  nonBillable,
  productivityPct,
  resourceCount,
  periodSummary,
  recordCount,
}: TechnologyBillingKpiStripProps) {
  const totalH = billable + nonBillable;
  const tiles = [
    {
      label: 'Billing hours',
      value: fmtHours(billable),
      sub: `${periodSummary} · billable ÷ logged`,
      accent: 'bg-blue-500',
      textColor: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: <Clock size={16} className="text-blue-500" />,
    },
    {
      label: 'Non-billing hours',
      value: fmtHours(nonBillable),
      sub:
        totalH > 0
          ? `${((nonBillable / totalH) * 100).toFixed(1)}% of total hours`
          : 'Internal / overhead',
      accent: 'bg-slate-400',
      textColor: 'text-slate-700',
      bg: 'bg-slate-50',
      icon: <Coffee size={16} className="text-slate-500" />,
    },
    {
      label: 'Productivity',
      value: `${productivityPct.toFixed(1)}%`,
      sub: 'Billable ÷ total logged hours',
      accent: 'bg-violet-500',
      textColor: 'text-violet-700',
      bg: 'bg-violet-50',
      icon: <Percent size={16} className="text-violet-500" />,
    },
    {
      label: 'Resources',
      value: String(resourceCount),
      sub: `${recordCount.toLocaleString()} row${recordCount !== 1 ? 's' : ''} in slice`,
      accent: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bg: 'bg-emerald-50',
      icon: <Users size={16} className="text-emerald-500" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="bg-white rounded-xl border border-slate-200 shadow-md p-4 flex gap-3 items-start"
        >
          <div
            className={`w-1 self-stretch rounded-full flex-none ${tile.accent}`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              {tile.label}
            </p>
            <p
              className={`text-xl font-bold tabular-nums leading-tight ${tile.textColor}`}
            >
              {tile.value}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              {tile.sub}
            </p>
          </div>
          <div
            className={`w-8 h-8 rounded-lg ${tile.bg} flex items-center justify-center flex-none`}
          >
            {tile.icon}
          </div>
        </div>
      ))}
    </div>
  );
}
